import { useEffect, useState, useSyncExternalStore, type ComponentType, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useHotkeys, type RegisterableHotkey } from '@tanstack/react-hotkeys';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';

type PanelComponent = ComponentType<{ projectId: string; onClose: () => void }>;

/**
 * The panel is loaded on demand, but WARMED before anyone clicks: on idle once
 * the trigger mounts, and again on pointerenter/focus of the trigger. Until the
 * module is in, the popover shows a same-size skeleton (never an empty box).
 *
 * Not `React.lazy`: a lazy component suspends on its first render even when
 * its import already resolved (the `.then` lands a microtask later), so a
 * preloaded panel would still flash its fallback. A tiny external store
 * hands back the real component synchronously once it is loaded.
 *
 * A failed import (stale chunk after a deploy, offline) resets the pending
 * promise, so the next hover/open retries instead of sticking on the skeleton.
 */
let loadedPanel: PanelComponent | null = null;
let pendingLoad: Promise<void> | null = null;
const listeners = new Set<() => void>();

export function preloadAnalysisMenuPanel(): Promise<void> {
  if (loadedPanel) return Promise.resolve();
  pendingLoad ??= import('./AnalysisMenuPanel').then(
    (m) => {
      loadedPanel = m.AnalysisMenuPanel;
      for (const notify of listeners) notify();
    },
    (err: unknown) => {
      pendingLoad = null;
      throw err;
    },
  );
  return pendingLoad;
}

const warmPanel = () => {
  preloadAnalysisMenuPanel().catch(() => {});
};

const subscribe = (notify: () => void) => {
  listeners.add(notify);
  return () => {
    listeners.delete(notify);
  };
};
const getLoadedPanel = () => loadedPanel;

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

/** Same footprint as the panel (search bar, header shortcut, a handful of rows) so
 *  the popover does not jump when the real content swaps in. */
function AnalysisMenuSkeleton() {
  const { t } = useTranslation('project');
  return (
    <div
      data-testid='analysis-menu-skeleton'
      role='status'
      aria-busy
      aria-label={t('analysisLauncher.chooser.loading')}
      className='flex min-w-0 flex-col'
    >
      <div className='flex shrink-0 items-center gap-2 border-b border-slate-200/70 p-2 dark:border-stone-700/40'>
        <Skeleton className='h-9 min-w-0 flex-1' />
        <Skeleton className='size-9 shrink-0' />
      </div>
      <div className='flex flex-col gap-1 p-1'>
        <Skeleton className='mx-3 mt-3 mb-1 h-3 w-20' />
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className='flex items-start gap-3 px-3 py-2'>
            <Skeleton className='mt-0.5 size-5 shrink-0' />
            <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
              <Skeleton className='h-4 w-2/3' />
              <Skeleton className='h-3 w-11/12' />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnalysisMenuContent({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const Panel = useSyncExternalStore(subscribe, getLoadedPanel, getLoadedPanel);
  // Opened before the idle preload got to run (or after a failed load).
  useEffect(() => {
    if (!Panel) warmPanel();
  }, [Panel]);
  return Panel ? <Panel projectId={projectId} onClose={onClose} /> : <AnalysisMenuSkeleton />;
}

/**
 * The analysis dropdown (spec amendment A2): `children` is the trigger
 * button; the panel opens anchored under it, right-aligned — a popover, not a
 * modal. Used by the project-header icon and the Analyses page's
 * "New analysis…" button.
 */
export function AnalysisMenuPopover({
  projectId,
  children,
  hotkey,
}: {
  projectId: string;
  children: ReactElement;
  hotkey?: RegisterableHotkey;
}) {
  const [open, setOpen] = useState(false);

  // Only the header opts in; other triggers can coexist on the Analyses page.
  useHotkeys(
    hotkey ? [{ hotkey, callback: () => setOpen((prev) => !prev) }] : [],
    { preventDefault: true, ignoreInputs: true },
  );

  useEffect(() => {
    const w = window as IdleWindow;
    if (w.requestIdleCallback) {
      const handle = w.requestIdleCallback(warmPanel, { timeout: 3000 });
      return () => w.cancelIdleCallback?.(handle);
    }
    const handle = window.setTimeout(warmPanel, 1500);
    return () => window.clearTimeout(handle);
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* Radix's Slot composes these with the trigger's own handlers. */}
      <PopoverTrigger asChild onPointerEnter={warmPanel} onFocus={warmPanel}>
        {children}
      </PopoverTrigger>
      <PopoverContent align='end' className='w-[26rem] max-w-[calc(100vw-1rem)] overflow-hidden p-0'>
        <AnalysisMenuContent projectId={projectId} onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
